import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import { useAuthStore } from './auth.store';

export const THEMES = [
    { value: 'slate', label: 'Slate (Đen Trắng)', color: '#0f172a' },
    { value: 'emerald', label: 'Emerald (Ngọc lục bảo)', color: '#059669' },
    { value: 'blue', label: 'Blue Solid', color: '#2563eb' },
    { value: 'violet', label: 'Violet Deep', color: '#7c3aed' },
    { value: 'red', label: 'Red Danger', color: '#dc2626' },
    { value: 'cyan', label: 'Cyan Ocean', color: '#0891b2' },
    { value: 'orange', label: 'Orange Warm', color: '#ea580c' },
];

export interface UserPreferences {
    menuLayout: 'dual-axis' | 'treeview';
    themeName: string;
    accentColor: string;
    themeMode: 'light' | 'dark' | 'auto';
    density: 'comfortable' | 'compact';
    sidebarMode: 'full' | 'collapsed' | 'auto';
    showWelcome: boolean;
    language: string;
    dateFormat: string;
    currencyDisplay: string;
    tablePageSize: number;
    /** Người dùng đã tự chọn số dòng / trang (ở Cài đặt hoặc trên một danh sách) — chưa chọn thì mỗi danh sách dùng mặc định riêng */
    tablePageSizeSet: boolean;
    notifications: boolean;
}

export const DEFAULTS: UserPreferences = {
    menuLayout: 'treeview',
    themeName: 'emerald',
    accentColor: '#059669',
    themeMode: 'light',
    density: 'comfortable',
    sidebarMode: 'full',
    showWelcome: true,
    language: 'vi',
    dateFormat: 'DD/MM/YYYY',
    currencyDisplay: 'symbol',
    tablePageSize: 25,
    tablePageSizeSet: false,
    notifications: true,
};

interface UserPreferencesStore extends UserPreferences {
    setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
    resetPreferences: () => void;
}

/** Apply menu layout attribute */
export function applyMenuLayout(menuLayout: 'dual-axis' | 'treeview') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-menu-layout', menuLayout);
    }
}

/** Apply a predefined theme by setting data-theme attribute */
export function applyTheme(themeName: string) {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', themeName);
    }
}

/** Apply accent color as CSS custom properties on :root */
export function applyAccentColor(hex: string) {
    if (typeof document !== 'undefined') {
        const r = document.documentElement;
        r.style.setProperty('--apv-accent', hex);
        r.style.setProperty('--apv-sidebar-active-bg', hex);
        r.style.setProperty('--apv-text-link', hex);
        r.style.setProperty('--apv-text-muted', hex);
    }
}

export function applyThemeMode(mode: 'light' | 'dark' | 'auto') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme-mode', mode);
        if (mode === 'dark' || (mode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }
}

export function applyDensity(density: 'comfortable' | 'compact') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-density', density);
        if (density === 'compact') {
            document.documentElement.classList.add('density-compact');
        } else {
            document.documentElement.classList.remove('density-compact');
        }
    }
}

const userPartitionedStorage: StateStorage = {
    getItem: (name) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        const val = localStorage.getItem(`${name}_${userId}`);
        if (val) return val;
        // Migration from v2 if user had explicitly customized preferences
        const oldVal = localStorage.getItem(`apv_user_prefs_v2_${userId}`);
        if (oldVal) {
            try {
                const parsed = JSON.parse(oldVal);
                const s = parsed.state || parsed;
                if (s && (s.themeName !== 'blue' || s.menuLayout !== 'dual-axis')) {
                    return oldVal;
                }
            } catch {
                // ignore
            }
        }
        return null;
    },
    setItem: (name, value) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        localStorage.setItem(`${name}_${userId}`, value);
    },
    removeItem: (name) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        localStorage.removeItem(`${name}_${userId}`);
    },
};

export const useUserPreferences = create<UserPreferencesStore>()(
    persist(
        (set) => ({
            ...DEFAULTS,
            setPreference: (key, value) => set({ [key]: value }),
            resetPreferences: () => set(DEFAULTS),
        }),
        {
            name: 'apv_user_prefs_v3',
            storage: createJSONStorage(() => userPartitionedStorage),
            onRehydrateStorage: () => (state) => {
                // Apply theme immediately after rehydration on client
                if (state) {
                    if (state.menuLayout) applyMenuLayout(state.menuLayout);
                    if (state.themeName) applyTheme(state.themeName);
                    if (state.accentColor) applyAccentColor(state.accentColor);
                    if (state.themeMode) applyThemeMode(state.themeMode);
                    if (state.density) applyDensity(state.density);
                }
            }
        }
    )
);

useAuthStore.subscribe((state, prevState) => {
    if (state.user?.id !== prevState.user?.id) {
        const userId = state.user?.id || 'guest';
        try {
            const saved = localStorage.getItem(`apv_user_prefs_v3_${userId}`) || localStorage.getItem(`apv_user_prefs_v2_${userId}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.state) {
                    // If old state was default blue/dual-axis, upgrade to new DEFAULTS
                    if (parsed.state.themeName === 'blue' && parsed.state.menuLayout === 'dual-axis') {
                        useUserPreferences.setState(DEFAULTS);
                        return;
                    }
                    useUserPreferences.setState(parsed.state);
                    return;
                }
            }
            useUserPreferences.setState(DEFAULTS);
        } catch {
            useUserPreferences.setState(DEFAULTS);
        }
    }
});

/** Synchronous initial load before React renders */
export function initPreferences() {
    try {
        const userId = useAuthStore.getState().user?.id || 'guest';
        const saved = localStorage.getItem(`apv_user_prefs_v3_${userId}`) || localStorage.getItem(`apv_user_prefs_v2_${userId}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            const prefs = parsed.state || parsed;
            // Upgrade if old default
            const effectiveLayout = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.menuLayout : (prefs.menuLayout || DEFAULTS.menuLayout);
            const effectiveTheme = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.themeName : (prefs.themeName || DEFAULTS.themeName);
            const effectiveAccent = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.accentColor : (prefs.accentColor || DEFAULTS.accentColor);

            applyMenuLayout(effectiveLayout);
            applyTheme(effectiveTheme);
            applyAccentColor(effectiveAccent);
            if (prefs.themeMode) applyThemeMode(prefs.themeMode);
            if (prefs.density) applyDensity(prefs.density);
        } else {
            applyMenuLayout(DEFAULTS.menuLayout);
            applyTheme(DEFAULTS.themeName);
            applyAccentColor(DEFAULTS.accentColor);
            applyThemeMode(DEFAULTS.themeMode);
            applyDensity(DEFAULTS.density);
        }
    } catch {
        // ignore parse errors
    }
}

// Subscribe to changes and apply them to the DOM
useUserPreferences.subscribe((state, prevState) => {
    if (state.menuLayout !== prevState.menuLayout) applyMenuLayout(state.menuLayout);
    if (state.themeName !== prevState.themeName) applyTheme(state.themeName);
    if (state.accentColor !== prevState.accentColor) applyAccentColor(state.accentColor);
    if (state.themeMode !== prevState.themeMode) applyThemeMode(state.themeMode);
    if (state.density !== prevState.density) applyDensity(state.density);
});
